import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAuthStore } from './auth'
import { mockCrud, loadFromStorage } from '@/services/mockData'
import { TIER_FEATURES } from '@/services/stripe'
import type { Location, LocationFormData, LocationInventory, LocationInventoryFormData } from '@/types'

const STORAGE_KEY = 'locations'
const INVENTORY_STORAGE_KEY = 'location_inventory'

const MOCK_LOCATIONS: Location[] = [
  {
    id: 'loc-1',
    user_id: 'demo-user-123',
    name: 'Downtown Art Gallery',
    address: '123 Main St, Portland, OR 97201',
    latitude: 45.5152,
    longitude: -122.6784,
    map_image_url: '',
    notes: 'Open Tues-Sat, consignment arrangement',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'loc-2',
    user_id: 'demo-user-123',
    name: 'Maker Market Booth',
    address: '456 Market Ave, Portland, OR 97209',
    latitude: 45.5231,
    longitude: -122.6765,
    map_image_url: '',
    notes: 'Weekend pop-up spot',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
]

const MOCK_INVENTORY: LocationInventory[] = [
  {
    id: 'inv-1',
    user_id: 'demo-user-123',
    location_id: 'loc-1',
    product_id: 'prod-1',
    quantity: 10,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'inv-2',
    user_id: 'demo-user-123',
    location_id: 'loc-1',
    custom_name: 'Handmade Bookmark',
    custom_price: 5.00,
    quantity: 25,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
]

export const useLocationStore = defineStore('location', () => {
  const locations = ref<Location[]>([])
  const inventory = ref<LocationInventory[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const _loaded = ref(false)
  const _contextKey = ref<string>('personal')
  const _inventoryLoaded = ref(false)

  const getById = (id: string) => locations.value.find(l => l.id === id)
  const inventoryByLocation = (locationId: string) =>
    inventory.value.filter(i => i.location_id === locationId)
  const inventoryCountByLocation = (locationId: string) =>
    inventory.value.filter(i => i.location_id === locationId).length

  // ─── Locations CRUD ──────────────────────────────────────────

  const fetchLocations = async (force = false, { silent = false, teamId }: { silent?: boolean; teamId?: string } = {}) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return

    const newContext = teamId || 'personal'
    if (newContext !== _contextKey.value) {
      _loaded.value = false
      _contextKey.value = newContext
    }

    if (_loaded.value && !force) return

    if (!silent) {
      loading.value = true
      error.value = null
    }

    try {
      if (authStore.isDemoMode) {
        await new Promise(resolve => setTimeout(resolve, 150))
        locations.value = loadFromStorage(STORAGE_KEY, MOCK_LOCATIONS)
        inventory.value = loadFromStorage(INVENTORY_STORAGE_KEY, MOCK_INVENTORY)
        _inventoryLoaded.value = true
      } else {
        const { supabase } = await import('@/services/supabase')

        // Fetch locations and all inventory in parallel
        let locQuery = supabase
          .from('locations')
          .select('*')
        if (teamId) {
          locQuery = locQuery.eq('team_id', teamId)
        } else {
          locQuery = locQuery.is('team_id', null).eq('user_id', authStore.userId)
        }

        let invQuery = supabase
          .from('location_inventory')
          .select('*')
        if (teamId) {
          invQuery = invQuery.eq('team_id', teamId)
        } else {
          invQuery = invQuery.is('team_id', null).eq('user_id', authStore.userId)
        }

        const [locRes, invRes] = await Promise.all([
          locQuery.order('created_at', { ascending: false }),
          invQuery.order('created_at', { ascending: false }),
        ])

        if (locRes.error) throw locRes.error
        if (invRes.error) throw invRes.error

        locations.value = locRes.data as Location[]
        inventory.value = invRes.data as LocationInventory[]
        _inventoryLoaded.value = true
      }
      _loaded.value = true
    } catch (err) {
      if (!silent) error.value = err instanceof Error ? err.message : 'Failed to fetch locations'
    } finally {
      if (!silent) loading.value = false
    }
  }

  const createLocation = async (locationData: LocationFormData, teamId?: string) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    // Tier limit check (skip for team context)
    if (!teamId) {
      const tierConfig = TIER_FEATURES[authStore.subscriptionTier]
      const limit = tierConfig.storeLocations
      if (limit !== -1 && locations.value.length >= limit) {
        return {
          success: false,
          error: `You've reached the limit of ${limit} location${limit === 1 ? '' : 's'} on the ${tierConfig.name} plan. Upgrade to add more.`,
        }
      }
    }

    loading.value = true
    try {
      if (authStore.isDemoMode) {
        const newLocation = mockCrud.create(STORAGE_KEY, {
          ...locationData,
          user_id: authStore.userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as Location, locations.value)
        locations.value.unshift(newLocation)
        return { success: true, data: newLocation }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: insertError } = await supabase
          .from('locations')
          .insert({ ...locationData, user_id: authStore.userId, ...(teamId ? { team_id: teamId } : {}) })
          .select()
          .single()

        if (insertError) throw insertError
        locations.value.unshift(data as Location)
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create location'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updateLocation = async (id: string, updates: Partial<LocationFormData>) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        const updated = mockCrud.update(STORAGE_KEY, id, updates, locations.value)
        if (updated) {
          const index = locations.value.findIndex(l => l.id === id)
          if (index !== -1) locations.value[index] = updated
        }
        return { success: true, data: updated }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: updateError } = await supabase
          .from('locations')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single()

        if (updateError) throw updateError
        const index = locations.value.findIndex(l => l.id === id)
        if (index !== -1) locations.value[index] = data as Location
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update location'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const deleteLocation = async (id: string) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        mockCrud.delete(STORAGE_KEY, id, locations.value)
        locations.value = locations.value.filter(l => l.id !== id)
        // Also remove inventory for this location
        inventory.value = inventory.value.filter(i => i.location_id !== id)
        return { success: true }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { error: deleteError } = await supabase.from('locations').delete().eq('id', id)
        if (deleteError) throw deleteError
        locations.value = locations.value.filter(l => l.id !== id)
        inventory.value = inventory.value.filter(i => i.location_id !== id)
        return { success: true }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete location'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // ─── Inventory CRUD ──────────────────────────────────────────

  const addInventoryItem = async (itemData: LocationInventoryFormData, teamId?: string) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }
    // Stock belongs to its location's team; without it a team item is saved as
    // personal and drops out of the team's list on the next load.
    const team = locations.value.find(l => l.id === itemData.location_id)?.team_id ?? teamId

    try {
      if (authStore.isDemoMode) {
        const newItem = mockCrud.create(INVENTORY_STORAGE_KEY, {
          ...itemData,
          user_id: authStore.userId,
          team_id: team ?? null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as LocationInventory, inventory.value)
        inventory.value.unshift(newItem)
        return { success: true, data: newItem }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: insertError } = await supabase
          .from('location_inventory')
          .insert({ ...itemData, user_id: authStore.userId, ...(team ? { team_id: team } : {}) })
          .select()
          .single()

        if (insertError) throw insertError
        inventory.value.unshift(data as LocationInventory)
        return { success: true, data }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add inventory item'
      return { success: false, error: msg }
    }
  }

  const updateInventoryItem = async (id: string, updates: Partial<LocationInventoryFormData>) => {
    const authStore = useAuthStore()

    try {
      if (authStore.isDemoMode) {
        const updated = mockCrud.update(INVENTORY_STORAGE_KEY, id, updates, inventory.value)
        if (updated) {
          const index = inventory.value.findIndex(i => i.id === id)
          if (index !== -1) inventory.value[index] = updated
        }
        return { success: true, data: updated }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: updateError } = await supabase
          .from('location_inventory')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single()

        if (updateError) throw updateError
        const index = inventory.value.findIndex(i => i.id === id)
        if (index !== -1) inventory.value[index] = data as LocationInventory
        return { success: true, data }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update inventory item'
      return { success: false, error: msg }
    }
  }

  const removeInventoryItem = async (id: string) => {
    const authStore = useAuthStore()

    try {
      if (authStore.isDemoMode) {
        mockCrud.delete(INVENTORY_STORAGE_KEY, id, inventory.value)
        inventory.value = inventory.value.filter(i => i.id !== id)
        return { success: true }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { error: deleteError } = await supabase.from('location_inventory').delete().eq('id', id)
        if (deleteError) throw deleteError
        inventory.value = inventory.value.filter(i => i.id !== id)
        return { success: true }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to remove inventory item'
      return { success: false, error: msg }
    }
  }

  const clearData = () => {
    locations.value = []
    inventory.value = []
    _loaded.value = false
    _inventoryLoaded.value = false
  }

  return {
    locations, inventory, loading, error, _loaded, _contextKey, _inventoryLoaded,
    getById, inventoryByLocation, inventoryCountByLocation,
    fetchLocations, createLocation, updateLocation, deleteLocation,
    addInventoryItem, updateInventoryItem, removeInventoryItem,
    clearData,
  }
})
